"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HyperText } from "@/components/ui/hyper-text";
import { TextAnimate } from "@/components/ui/text-animate";
import { supabase } from "@/lib/supabase";
import Image from "next/image";
import { useState } from "react";

const IS_PRODUCTION =
  process.env.NEXT_PUBLIC_ENVIRONMENT === "production" ||
  process.env.NEXT_PUBLIC_ENVIRONMENT === "prd";


export function SignInScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSSOSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      const { data, error: authError } = await supabase.auth.signInWithSSO({
        domain: "woods.co.nz",
      });

      if (authError) {
        setError(authError.message);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) {
      setError(authError.message);
    }

    setLoading(false);
  };

  return (
    <div className="flex flex-1 items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 px-4">
        <div className="space-y-1 text-center">
          <HyperText
            as="h1"
            className="justify-center text-2xl font-semibold tracking-tight text-foreground py-0"
            duration={600}
            animateOnHover
          >
            Woods AI
          </HyperText>
          <TextAnimate
            as="p"
            animation="blurInUp"
            by="word"
            delay={0.3}
            className="text-sm text-muted-foreground"
          >
            Sign in to continue
          </TextAnimate>
        </div>

        {IS_PRODUCTION ? (
          <div className="space-y-3">
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <Button
              className="w-full gap-2"
              onClick={handleSSOSignIn}
              disabled={loading}
            >
              <Image
                src="/woods-icon.svg"
                alt="Woods Logo"
                width={20}
                height={20}
                className="size-5 rounded-full object-cover"
              />
              {loading ? "Connecting..." : "Login with Woods"}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              data-testid="sign-in-email"
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              data-testid="sign-in-password"
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button data-testid="sign-in-submit" type="submit" className="w-full" disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
